import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import {
    Checkbox,
    Col,
    ControlLabel,
    FormControl,
    FormGroup,
    InputGroup,
    Row
} from "react-bootstrap";
import Select from "react-select";
import { Event } from "../../../Comet";
import { useEventContext } from '../../../../context';
import { getHumanDuration } from "tools";
import { camelCase } from '../../../../../platform/src/helpers/camelcase';
import {
    getEpisode,
    updateEpisode,
    updateEpisodePoster
} from "../../../../../api/cp";
import Loading from "../../../Loading";
import FormControlLanguage from "../../../UI/FormControlLanguage";
import Tooltip from "../../../UI/Tooltip";
import { getDayStart } from "../../../../helpers/timeHelper";
import { checkIntersection } from "../../../../helpers/timeHelper";

const Episode = ({ episode, episodes, streamDaysOptions = [], streamTimeStart, streamTimeStop }) => {
    const [currentEpisode, setCurrentEpisode] = useState(episode);
    const [dataLoaded, setDataLoaded] = useState(false);
    const [error, setError] = useState({});
    const isError = Object.keys(error).some(key => error[key]);
    const [episodeData, updateEpisodeData] = React.useReducer(
        (prev, next) => {
            return { ...prev, ...next };
        },
        {
            timeStart: null,
            timeStop: null,
            name_ru: null,
            name_en: null,
            info_ru: null,
            info_en: null,
            preview: null,
            section_ru: null,
            section_en: null,
            displayParticipationType: null,
            importantEpisode: null,
            paintInStreamColor: null,
            expandReports: false,
            hidden: false,
            participationType: false,
            delayOpacity: false,
            delayBlur: false,
            displayTimeLarge: false,
            displayNameLarge: false,
            hideStreamColor: false,
            hideStreamDescription: false,
            displayTime: true,
        }
    );

    const posterInputRef = useRef(null);

    const { event } = useEventContext();

    useEffect(() => {
        setCurrentEpisode({ ...episode });
        handleUpdateEpisodeData(episode);
        setDataLoaded(true);

        if (!episode.displayParticipationType && event.participationType !== 'hybrid') {
            episodeData.displayParticipationType !== event.participationType && handleChangeSelectValue('displayParticipationType', event.participationType);
        }
    }, [episode]);

    useEffect(() => {
        const episodeStartEnd = { start: episodeData.timeStart, end: episodeData.timeStop };
        const _error = {
            isIntersecting: episodes.filter(({ id }) => currentEpisode?.id !== id).some(({ timeStart, timeStop }) => checkIntersection(episodeStartEnd, { start: timeStart, end: timeStop })),
            outOfRange: episodeData.timeStart < streamTimeStart || episodeData.timeStop > streamTimeStop,
        }
        setError(_error)
    }, [episodeData, episodes, streamTimeStart, streamTimeStop]);

    const handleSave = async (newData) => {
        
        const data = Object.keys(newData).reduce((acc, key) => {
            if (newData[key] !== currentEpisode[key]) {
                acc[camelCase(key)] = newData[key];
            }

            return acc;
        }, {});

        if (Object.keys(data).length === 0) {
            return;
        }

        try {
            const result = await updateEpisode(currentEpisode.id, data);
            setCurrentEpisode(result);
            updateEpisodeData(result);
        } catch (error) {
            if (error.message) {
                alert(error.message);
                updateEpisodeData({ timeStart: currentEpisode.timeStart });
                updateEpisodeData({ timeStop: currentEpisode.timeStop });
            }
        }
    };

    const handleChangeSelectValue = async (key, value) => {
        if (currentEpisode[key] === value) {
            return;
        }

        const result = await updateEpisode(currentEpisode.id, { [key]: value });
        setCurrentEpisode({ ...result });
    };

    const onPosterFileChange = (e) => {
        if (e.target.files.length === 0) {
            return;
        }
        const file = e.target.files[0];
        if (!file.type.match(/^image/)) {
            return;
        }

        updateEpisodePoster(currentEpisode.id, file)
            .then(({ preview }) => {
                updateEpisodeData({ preview: preview + '?t=' + Math.random() });
            });
        e.target.value = '';
    };

    const handleSetValue = (dataKey, value, max, min = 0) => {
        updateEpisodeData({ [dataKey]: value });
    };

    const handleCometEpisodeUpdate = (cometEpisode) => {
        setCurrentEpisode({ ...cometEpisode });
        handleUpdateEpisodeData(cometEpisode);
    };

    const handleUpdateEpisodeData = (episode) => {
        const data = Object.keys(episodeData).reduce((acc, key) => {
            if (episodeData[key] !== episode[key]) {
                acc[key] = episode[key];
            }

            return acc;
        }, {});

        updateEpisodeData(data);
    };

    const changeEpisodeDay = (targetDay) => {
        const episodeDayStart = moment(getDayStart(episodeData.timeStart)).unix();
        const targetDayStart = moment(getDayStart(moment(targetDay).unix())).unix();
        updateEpisodeData({
            timeStart: episodeData.timeStart - episodeDayStart + targetDayStart,
            timeStop: episodeData.timeStop - episodeDayStart + targetDayStart,
        })
    }

    if (!dataLoaded) {
        return null;
    }

    return (
        <section style={{ marginTop: '12px' }}>
            <Event event="update:episode" handler={handleCometEpisodeUpdate} />
            <Row>
                <Col md={6}>
                    <FormGroup>
                        <Row style={{ backgroundColor: isError ? 'rgba(225, 80, 70, 0.2)' : 'white', padding: '1rem 0', borderRadius: '0.5rem' }}>
                            {streamDaysOptions.length > 1 &&
                                <Col md={3}>
                                    <ControlLabel>Дата</ControlLabel>
                                    <Select
                                        value={streamDaysOptions.find(({ value }) => moment(value).isSame(moment.unix(episodeData.timeStart), 'day'))}
                                        onChange={({ value }) => changeEpisodeDay(value)}
                                        options={streamDaysOptions}
                                        onBlur={() => !isError && handleSave({timeStart: episodeData.timeStart, timeStop: episodeData.timeStop})}
                                    />
                                </Col>
                            }
                            <Col md={3}>
                                <ControlLabel>Начало</ControlLabel>
                                <FormControl
                                    type="time"
                                    step={1}
                                    value={moment.unix(episodeData.timeStart).format('HH:mm:ss')}
                                    onChange={(e) => {
                                        let [hours, minutes, seconds] = e.target.value.split(':');
                                        let time = moment.unix(episodeData.timeStart);
                                        time.hours(hours);
                                        time.minutes(minutes);
                                        time.seconds(seconds);
                                        updateEpisodeData({ timeStart: time.unix() });
                                        if (time.unix() > episodeData.timeStop) {
                                            updateEpisodeData({ timeStop: time.unix() });
                                        }
                                    }}
                                    onBlur={() => !isError && handleSave({timeStart: episodeData.timeStart, timeStop: episodeData.timeStop})}
                                />
                            </Col>
                            <Col md={3}>
                                <FormGroup validationState={episodeData.timeStop < episodeData.timeStart ? "error" : null}>
                                    <ControlLabel>Конец</ControlLabel>
                                    <FormControl
                                        type="time"
                                        step={1}
                                        value={moment.unix(episodeData.timeStop).format('HH:mm:ss')}
                                        onChange={(e) => {
                                            let [hours, minutes, seconds] = e.target.value.split(':');
                                            let time = moment.unix(episodeData.timeStart);
                                            time.hours(hours);
                                            time.minutes(minutes);
                                            time.seconds(seconds);
                                            updateEpisodeData({ timeStop: time.unix() });
                                        }}
                                        onBlur={() => !isError && handleSave({ timeStop: episodeData.timeStop})}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={3}>
                                <ControlLabel>Длина</ControlLabel>
                                <FormControl
                                    type="text"
                                    value={getHumanDuration(episodeData.timeStop - episodeData.timeStart, true)}
                                    onChange={(e) => {
                                        if (!e.target.value.match(/^\d+:\d{2}:\d{2}$/)) {
                                            e.target.value = getHumanDuration(episodeData.timeStop - episodeData.timeStart);
                                            return;
                                        }
                                        let [hours, minutes, seconds] = e.target.value.split(':');
                                        let diff = hours * 3600 + minutes * 60 + seconds * 1;
                                        updateEpisodeData({ timeStop: episodeData.timeStart + diff })
                                    }}
                                    onBlur={() => !isError && handleSave({ timeStop: episodeData.timeStop})}
                                />
                            </Col>
                            {isError && <Col md={12} style={{ color: '#EA5353', textAlign: 'center' }}>
                                <span>{error.isIntersecting ? "Эпизод пересекается по времени с другим эпизодом" : 'Эпизод выходит за пределы зала'}</span>
                            </Col>}
                        </Row>
                        <Row>
                            <Col md={6}>
                                <ControlLabel>
                                    Формат в программе/расписании
                                    <Tooltip width="300px">
                                        Настройка влияет на то, с каким флагом будет отображаться эпизод в программе на платформе и в виджетах
                                    </Tooltip>
                                </ControlLabel>
                                <FormControl
                                    componentClass={"select"}
                                    value={episodeData.displayParticipationType || ''}
                                    disabled={false}
                                    onChange={(e) => {
                                        handleSave({ displayParticipationType: e.target.value.length ? e.target.value : null });
                                    }}
                                >
                                    <option value={"hybrid"}>Онлайн + Офлайн</option>
                                    <option value={"online"}>Онлайн</option>
                                    <option value={"offline"}>Офлайн</option>
                                </FormControl>
                            </Col>
                            <Col md={6}>
                                <ControlLabel>
                                    Для кого отображаем в программе/расписании?
                                    <Tooltip width="300px">
                                        Настройка влияет на то, для зрителей с каким форматом участия (формат, который был выбран при регистрации) показывать данный эпизод в программе на платформе, в расписании зала и трансляции (плеере).
                                    </Tooltip>
                                </ControlLabel>
                                <FormControl
                                    componentClass={"select"}
                                    value={episodeData.participationType || ''}
                                    disabled={false}
                                    onChange={(e) => {
                                        handleSave({ participationType: e.target.value });
                                    }}
                                >
                                    <option value={"hybrid"}>Всем</option>
                                    <option value={"online"}>Онлайн участникам</option>
                                    <option value={"offline"}>Офлайн участникам</option>
                                </FormControl>
                            </Col>
                        </Row>
                    </FormGroup>
                    <FormGroup>
                        <ControlLabel>Название</ControlLabel>
                        <FormControlLanguage
                            type="text"
                            values={{
                                ru: episodeData.name_ru,
                                en: episodeData.name_en,
                            }}
                            onChange={{
                                ru: (e) => updateEpisodeData({ name_ru: e.target.value }),
                                en: (e) => updateEpisodeData({ name_en: e.target.value }),
                            }}
                            onBlur={() => handleSave({ name_ru: episodeData.name_ru, name_en: episodeData.name_en})}
                        />
                    </FormGroup>
                    <FormGroup>
                        <ControlLabel>{currentEpisode.delay ? 'Подзаголовок' : 'Аннотация'}</ControlLabel>
                        <FormControlLanguage
                            type="textarea"
                            rows={7}
                            values={{
                                ru: episodeData.info_ru,
                                en: episodeData.info_en,
                            }}
                            onChange={{
                                ru: (e) => updateEpisodeData({ info_ru: e.target.value }),
                                en: (e) => updateEpisodeData({ info_en: e.target.value }),
                            }}
                            onBlur={() => handleSave({ info_ru: episodeData.info_ru, info_en: episodeData.info_en})}
                        />
                    </FormGroup>
                </Col>
                <Col md={6}>
                    <FormGroup>
                        <ControlLabel>Постер</ControlLabel>
                        <FormControl
                            type="file"
                            style={{ display: 'none' }}
                            inputRef={ref => posterInputRef.current = ref}
                            onChange={onPosterFileChange}
                        />
                        <div
                            style={{
                                width: 320,
                                height: 180,
                                backgroundImage: 'url(' + (episodeData.preview || '/player/images/comdi/preview.png') + ')',
                                backgroundPosition: 'center',
                                backgroundRepeat: 'no-repeat',
                                backgroundSize: 'contain',
                                cursor: 'pointer',
                                border: 'solid 1px rgba(0, 0, 0, .2)',
                                borderRadius: 4,
                            }}
                            onClick={() => posterInputRef.current?.click()}
                        />
                    </FormGroup>
                    {currentEpisode.delay
                        ? <>
                            <FormGroup>
                                <InputGroup>
                                    <InputGroup.Addon>Прозрачность плитки</InputGroup.Addon>
                                    <FormControl
                                        type="number"
                                        name="delayOpacity"
                                        style={{ width: '80px' }}
                                        value={episodeData.delayOpacity || 80}
                                        onChange={(e) => handleSetValue('delayOpacity', +e.target.value, 100)}
                                        onBlur={() => handleSave({delayOpacity: episodeData.delayOpacity})}
                                    />
                                </InputGroup>
                                <p style={{ marginTop: '4px', fontStyle: 'italic' }}>
                                    Введите значение от 0 до 100
                                </p>
                            </FormGroup>
                            <FormGroup>
                                <InputGroup>
                                    <InputGroup.Addon>Размытие плитки</InputGroup.Addon>
                                    <FormControl
                                        type="number"
                                        name="delayBlur"
                                        style={{ width: '60px' }}
                                        value={episodeData.delayBlur || 5}
                                        onChange={(e) => handleSetValue('delayBlur', +e.target.value)}
                                        onBlur={() => handleSave({delayBlur: episodeData.delayBlur})}
                                    />
                                </InputGroup>
                            </FormGroup>
                        </>
                        : <>
                            <FormGroup>
                                <Checkbox
                                    checked={!!episodeData.expandReports}
                                    name="expandReports"
                                    onChange={(e) => handleSave({ expandReports: e.target.checked })}
                                >
                                    Показывать эпизод всегда в развёрнутом виде, даже если настройка вида стоит галочка
                                </Checkbox>
                            </FormGroup>
                            <FormGroup>
                                <Checkbox
                                    checked={!!episodeData.hidden}
                                    name="hidden"
                                    onChange={(e) => handleSave({ hidden: e.target.checked })}
                                >
                                    Скрыть эпизод из сетки программы
                                </Checkbox>
                            </FormGroup>
                            <FormGroup>
                                <Checkbox
                                    checked={!!episodeData.hideStreamDescription}
                                    name="hidden"
                                    onChange={(e) => handleSave({ hideStreamDescription: e.target.checked })}
                                >
                                    Cкрывать у плитки название зала
                                </Checkbox>
                            </FormGroup>
                            <FormGroup>
                                <Checkbox
                                    checked={!!episodeData.hideStreamColor}
                                    name="hidden"
                                    onChange={(e) => handleSave({ hideStreamColor: e.target.checked })}
                                >
                                    Скрывать у плитки цвет зала
                                </Checkbox>
                            </FormGroup>
                        </>
                    }
                    <FormGroup>
                        <Checkbox
                            checked={!!episodeData.importantEpisode}
                            name="importantEpisode"
                            onChange={(e) => handleSave({ importantEpisode: e.target.checked })}
                        >
                            Важный {!currentEpisode.delay ? 'эпизод' : 'перерыв'}
                        </Checkbox>
                    </FormGroup>
                    {
                        (episodeData.importantEpisode && !currentEpisode.delay) &&
                        <FormGroup>
                            <Checkbox
                                checked={!!episodeData.paintInStreamColor}
                                name="paintInStreamColor"
                                onChange={(e) => handleSave({ paintInStreamColor: e.target.checked })}
                            >
                                Закрашивать важный эпизод в цвет зала
                            </Checkbox>
                        </FormGroup>
                    }
                    {
                        currentEpisode.delay &&
                        <FormGroup>
                            <Checkbox
                                checked={!!episodeData.displayTime}
                                name="displayTime"
                                onChange={(e) => handleSave({ displayTime: e.target.checked })}
                            >
                                Отображать время
                            </Checkbox>
                        </FormGroup>
                    }
                    {((currentEpisode.delay && episodeData.displayTime) || episodeData.importantEpisode) &&
                        <FormGroup>
                            <Checkbox
                                checked={!!episodeData.displayTimeLarge}
                                name="displayTimeLarge"
                                onChange={(e) => handleSave({ displayTimeLarge: e.target.checked })}
                            >
                                Отображать время крупно
                            </Checkbox>
                        </FormGroup>
                    }
                    {(currentEpisode.delay || episodeData.importantEpisode) && (
                        <>
                            <FormGroup>
                                <Checkbox
                                    checked={!!episodeData.displayNameLarge}
                                    name="displayNameLarge"
                                    onChange={(e) => handleSave({ displayNameLarge: e.target.checked })}
                                >
                                    Отображать название крупно
                                </Checkbox>
                            </FormGroup>
                        </>
                    )}
                </Col>
            </Row>
        </section>
    );
}

export default ({ id, episodes, stream, streamDaysOptions, members, onShowMembers }) => {
    const [episode, setEpisode] = useState(null);

    useEffect(() => {
        setEpisode(null);
        if (!id) {
            return;
        }
        getEpisode(id)
            .then(setEpisode)
            ;
    }, [id]);

    if (null === episode) {
        return <Loading />
    }

    return <Episode
        episode={episode}
        episodes={episodes}
        streamDaysOptions={streamDaysOptions}
        streamTimeStart={stream.timeStart}
        streamTimeStop={stream.timeStop}
        members={members}
        onShowMembers={onShowMembers}
    />
}
